import { FC, memo, useCallback, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Empty, Flex, Row } from 'antd';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useUIDispatchContext } from 'providers/UI';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { fetchTranslatorPlans } from 'services/api/translatorPlans';
import { TranslatorPlan } from 'types/api/translatorPlans';

import useOrgEffect from 'hooks/useOrgEffect';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private } from 'components/shared';
import { Loading, PageWrapper } from 'components/ui';
import { Filters, PlanItem, TranslationPlansDetails } from './components';
import { StyledPagination } from './styled';

const PAGE_LIMIT = 9;

const Plans: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') || 1);
  const duration = Number(searchParams.get('duration')) || null;
  const search = searchParams.get('search') || '';

  const { toggleModal } = useUIDispatchContext();

  const [results, setResults] = useState<TranslatorPlan[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchTranslatorPlans({
          page,
          limit: PAGE_LIMIT,
          name: search || undefined,
          duration: duration ?? undefined
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, search, duration, refreshKey]);

  const triggerRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return (
    <PageWrapper
      title={t('translation_plans.page_title')}
      color="text-color-5"
      extra={[
        <Private
          route={PERMISSION_ROUTES.translatorPlans}
          action={PERMISSION_ACTIONS.create}
          key="add-translator"
        >
          <Button
            type="primary"
            onClick={() => toggleModal('translationPlans', true)}
          >
            <PlusOutlined />
            {t('translation_plans.add_new')}
          </Button>
        </Private>
      ]}
    >
      <Flex vertical>
        <Flex style={{ width: '100%' }} justify="start">
          <Filters />
        </Flex>
        {results.length ? (
          <>
            <Row gutter={[16, 16]} wrap>
              {results.map(item => (
                <Col sm={8} key={`plan-item-${item.id}`}>
                  <PlanItem
                    id={item.id}
                    name={item.name}
                    duration={item.duration}
                    price={item.price}
                    translation={`${item.lang_1} - ${item.lang_2}`}
                    onDeleted={triggerRefresh}
                  />
                </Col>
              ))}
            </Row>
            <StyledPagination
              style={{ marginTop: 32 }}
              pageSize={9}
              defaultCurrent={1}
              current={page}
              total={total}
              showSizeChanger={false}
              onChange={p => {
                searchParams.set('page', `${p}`);
                setSearchParams(searchParams);
              }}
            />
          </>
        ) : loading ? (
          <Loading />
        ) : (
          <Row justify="center">
            <Empty />
          </Row>
        )}
        <TranslationPlansDetails results={results} onSaved={triggerRefresh} />
      </Flex>
    </PageWrapper>
  );
};

export default memo(Plans);
